import { Button } from '@mantine/core'
import { useFeatureGate } from '@statsig/react-bindings'
import { ReactQuery } from 'cms-api'
import { Formik } from 'formik'
import { get } from 'lodash'
import React from 'react'
import { CUSTOM_CONTENT, LEAD_TIME_MAX_ORDERABLE_QTY } from 'shared-constants'
import { DATA_TEST_CLASS } from 'shared-constants/build/testIds'
import { ProductQuantityCounterField } from '@public/components/ProductQuantityCounterField'
import { useProductVariantQuantity } from '@public/hooks'
import { Box } from 'components/Layout'
import { type CartContextInterface } from 'providers/cart'
import { useGoogleAnalytics } from 'providers/googleAnalytics'
import { useUserAuth } from 'providers/userAuth'
import { FONT_SIZE, FONT_WEIGHT, SPACE } from 'Theme'
import {
  ProductAvailability,
  ProductStatus,
  ProductVariantStatus,
  type PublicProductQuery,
} from 'types/graphql-generated'
import { catchHandler } from 'utils/catchHandler'
import { klaviyoTrackAddToCartShowroom } from 'utils/klaviyo'
import { getVariantsLeadTime, onAddItemToCartTracking } from '../../utils'
import { AddToCartButton } from './AddToCartButton'
import {
  getProductHeaderValidationSchema,
  PRODUCT_HEADER_FIELD_NAMES,
} from './productHeaderValidationSchema'
import { Title } from './Title'
import { VariantDescription } from './VariantDescription'
import { VariantDetails } from './VariantDetails'
import { VariantPicker } from './VariantPicker'

const trackAddOnCartHandler = ({
  productId,
  productVariantId,
  productVariantSku,
}) => {
  klaviyoTrackAddToCartShowroom({
    productId,
    productVariantId,
    productVariantSku,
  })
}

interface ProductHeaderProps {
  product: PublicProductQuery['product']
  isLoading: boolean
  cartQuantity: number
  openCartModal: ({ addedQuantity }: { addedQuantity: number }) => void
  isSetItemToCartLoading: boolean
  setItem: CartContextInterface['setItem']
  detailsRef: React.RefObject<HTMLDivElement>
  selectedVariant: PublicProductQuery['product']['variants'][0]
  variants: PublicProductQuery['product']['variants']
  cartId: number
  onVariantChange: (
    optionName: string,
    value: string,
    isUnavailable: boolean
  ) => void
  isPreview: boolean
}

export const ProductHeader: React.FC<React.PWC<ProductHeaderProps>> = ({
  product,
  cartQuantity,
  openCartModal,
  isSetItemToCartLoading,
  setItem,
  isLoading,
  detailsRef,
  selectedVariant,
  variants,
  cartId,
  onVariantChange,
  isPreview,
}) => {
  const { executeDataToDataLayer } = useGoogleAnalytics()
  const { user, isPageLoading } = useUserAuth()
  const { id: productId, title, brand } = product
  const {
    id,
    sku,
    price,
    tradePrice,
    quantity,
    managedCustomFields,
    availability,
    customContent,
  } = selectedVariant
  const leadTime = getVariantsLeadTime({
    managedCustomFields,
  })
  const variantQuantity = leadTime ? LEAD_TIME_MAX_ORDERABLE_QTY : quantity
  const availableQuantity = variantQuantity - cartQuantity
  const isPriceUponRequest = price.total === 0
  const { isCounterEnabled, minimumOrderQuantity, quantityUnit } =
    useProductVariantQuantity({ managedCustomFields })
  const { data: blackFridayPromoCode } =
    ReactQuery.useGetPromoCodesBlackFriday()

  const showFavoritesButtonInPDP = useFeatureGate(
    'show_favorites_button_in_pdp'
  )
  const {
    data: favoriteUserProductVariant,
    refetch: refetchFavoriteUserProductVariant,
  } = ReactQuery.useGetFavoriteUserProductVariantBookmarkLegacyProductVariantId(
    id.toString(),
    {
      query: {
        enabled: showFavoritesButtonInPDP.value,
      },
    }
  )
  const { mutateAsync: mutatePutFavoriteUserProductVariantBookmark } =
    ReactQuery.usePutFavoriteUserProductVariantBookmark({
      mutation: {
        onSettled: () => refetchFavoriteUserProductVariant(),
      },
    })

  const hasPublishedStatus =
    product?.status === ProductStatus.Published &&
    selectedVariant?.status === ProductVariantStatus.Published

  // This is a temporary fix which will be further addressed in https://github.com/sudolabs-io/the-expert/issues/6172
  const isArchived =
    product?.status === ProductStatus.Archived ||
    selectedVariant?.status === ProductVariantStatus.Archived

  // This ensures that if you have more than minimumOrderQuantity in cart, you can add whatever quantity you want to cart
  const minimumCounterValue =
    cartQuantity >= minimumOrderQuantity ? 1 : minimumOrderQuantity

  const aboveTheFold = get(selectedVariant, [
    'customContent',
    CUSTOM_CONTENT.ABOVE_THE_FOLD,
  ])

  const shouldDisplayBlackFridayBadge =
    brand &&
    availability &&
    !isPageLoading &&
    !user?.isTradeCapable &&
    availability !== ProductAvailability.SoldOut &&
    blackFridayPromoCode?.data &&
    !blackFridayPromoCode.data.exclusionBrands?.some(
      ({ name }) => name === brand?.id
    )

  const isBookmarked = !!favoriteUserProductVariant?.data?.isBookmarked

  return (
    <>
      <Title
        brand={brand}
        title={title}
        price={price}
        tradePrice={tradePrice}
        quantityUnit={quantityUnit}
        isPriceVisible={hasPublishedStatus || isPreview}
        discount={
          shouldDisplayBlackFridayBadge
            ? `Use code ${blackFridayPromoCode.data.code} for Black Friday discount`
            : undefined
        }
      />
      {showFavoritesButtonInPDP.value && (
        <Button
          onClick={() => {
            mutatePutFavoriteUserProductVariantBookmark({
              data: {
                isBookmarked: !isBookmarked,
                legacyProductVariantId: id,
              },
            })
          }}
        >
          {isBookmarked ? 'Unbookmark' : 'Bookmark'}
        </Button>
      )}
      <VariantDescription
        availableQuantity={availableQuantity}
        minimumOrderQuantity={minimumCounterValue}
        detailsRef={detailsRef}
        isPriceUponRequest={isPriceUponRequest}
        customDescription={aboveTheFold}
        leadTime={leadTime}
        availability={availability}
        enquire={customContent?.enquire}
        hasPublishedStatus={hasPublishedStatus}
      />

      <VariantPicker
        variants={variants}
        selectedVariant={selectedVariant}
        onVariantChange={onVariantChange}
      />
      <Formik
        onSubmit={async ({ quantity: addedQuantity }, { resetForm }) => {
          const response = await setItem({
            quantity: cartQuantity + addedQuantity,
            productVariantId: id,
          })
          if (!response.errors && !isLoading) {
            onAddItemToCartTracking({
              executeDataToDataLayer,
              quantity: addedQuantity,
              productVariant: selectedVariant,
              cartId: cartId || get(response, ['data', 'setItemToCart', 'id']),
              itemListName: get(
                response,
                [
                  'data',
                  'setItemToCart',
                  'items',
                  '0',
                  'metadata',
                  'ga4',
                  'listName',
                ],
                null
              ),
            })
            openCartModal({ addedQuantity })
            resetForm({
              values: {
                quantity: 1,
              },
            })
            if (user?.contactEmail) {
              catchHandler(trackAddOnCartHandler)({
                productId,
                productVariantId: id,
                productVariantSku: sku,
              })
            }
          }
        }}
        initialValues={{
          quantity: minimumCounterValue,
        }}
        validationSchema={getProductHeaderValidationSchema({
          availableQuantity,
          minimumOrderQuantity: minimumCounterValue,
          leadTime,
        })}
        validateOnChange
        enableReinitialize
      >
        {({ handleSubmit }) => (
          <>
            {(isCounterEnabled || minimumOrderQuantity > 1) &&
              hasPublishedStatus &&
              availableQuantity > minimumCounterValue && (
                <Box mb={SPACE.PX_20}>
                  <ProductQuantityCounterField
                    data-test-id={
                      DATA_TEST_CLASS.SHOWROOM_PRODUCT_DETAIL_COUNTER
                    }
                    id={id.toString()}
                    name={PRODUCT_HEADER_FIELD_NAMES.QUANTITY}
                    minimumOrderQuantity={minimumCounterValue}
                    availableQuantity={availableQuantity}
                    quantityUnit={quantityUnit}
                    textProps={{
                      textTransform: 'uppercase',
                      letterSpacing: '0.1em',
                      fontSize: FONT_SIZE.PX_12,
                      fontWeight: FONT_WEIGHT.MEDIUM,
                      mt: SPACE.PX_8,
                    }}
                  />
                </Box>
              )}
            <AddToCartButton
              productVariantId={id}
              productId={productId}
              isAddToCartDisabled={variantQuantity <= cartQuantity}
              isAddToCartLoading={isSetItemToCartLoading}
              isPriceUponRequest={isPriceUponRequest}
              onAddToCart={handleSubmit}
              isSoldOut={
                variantQuantity === 0 ||
                minimumCounterValue > variantQuantity ||
                isArchived
              }
              width="100%"
              isVariantPublished={hasPublishedStatus || isPreview}
              availability={availability as ProductAvailability}
            />
          </>
        )}
      </Formik>
      <VariantDetails
        selectedVariant={selectedVariant}
        brand={brand}
        detailsRef={detailsRef}
      />
    </>
  )
}
